import { useState, useRef, useMemo, useEffect } from 'react';
import {
  useForm,
  Controller,
  Path,
  ControllerRenderProps,
  FieldErrors,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseIcon } from '$icons/uncolored/Close.svg';
import {
  companySlice,
  EditPositionEnhancedInput,
  updateStatePosition,
  useDuplicatePositionEmployerMutation,
  useDuplicatePositionInternalMutation,
  useDuplicatePositionWithCustomRosterMutation,
  useEditPositionEnhancedMutation,
  useLoadCompaniesQuery,
  useLoadRosterQuery,
  useSavePositionEnhancedMutation,
} from '$store/modules/companies';
import PositionDto from '$types/serverTypes/positionDto';
import PositionInternalDto from '$types/serverTypes/positionInternalDto';
import {
  DuplicateRosterPositionValues,
  EditPositionFormDto,
} from '$types/serverTypes/editPositionDto.d';
import { SelectButton } from 'primereact/selectbutton';
import {
  IdealStartDateValues,
  SeniorityLevelType,
  TypesOfWork,
  WorkingDaysValues,
  WorkLocationTypes,
  TIMEZONES,
} from '$types/position/positionPropertyValues';

import {
  useGetJobPositionsIdAndTitleQuery,
  useGetSkillsQuery,
} from '$store/modules/form-info';
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from 'primereact/autocomplete';
import { InputTextarea } from 'primereact/inputtextarea';
import { JobPosition, RosterData } from '$types/candidates';
import { AutocompleteOption } from '$types/user_info';
import { InputText } from 'primereact/inputtext';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import getUserType from '$utils/getUserType';
import { UserTypes } from '$types/UserTypes';
import { useAppDispatch } from '$hooks/use-app-dispatch.hook';
import { ReactComponent as ArrowUp } from '$icons/CloseArrowColor.svg';
import { ReactComponent as ArrowDown } from '$icons/OpenArrowColor.svg';
import EducationDegreeTypes from '$types/DegreeTypes';
import { RadioButton } from 'primereact/radiobutton';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FloatLabel } from 'primereact/floatlabel';
import {
  MINIMUM_BASE_COMPENSATION,
  getTenantDefaultFee,
} from '$constants/SimeraOperations';
import { Checkbox } from 'primereact/checkbox';
import SPKToast from '$components/SPKToast/SPKToast';
import { userLanguage, ValidLanguages } from '$translations/index';
import { SuccessMessageEn, SuccessMessageEs } from '$constants/alerts';
import {
  expYearsToSeniority,
  seniorityToExpYears,
} from '$utils/seniorityToExpYears';
import { useGetAllTenantsQuery } from '$store/modules/tenant';
import { useGetAllSmartInterviewsQuery } from '$store/modules/smart-interviews';
import { useGetAllLanguagesQuery } from '$store/modules/language';
import { MultiSelect } from 'primereact/multiselect';
import PrimeReactEditor from '$components/UI/PrimeReactEditor/PrimeReactEditor';
import { useGetAllCountriesQuery } from '$store/modules/countries';

export enum SPKJobModalActionsTypes {
  duplicate,
  edit,
  create,
}

const SPKJobModal = ({
  position,
  closeHandler,
  onDuplicate,
  action,
}: {
  position: PositionDto | PositionInternalDto;
  closeHandler: () => void;
  onDuplicate?: () => void;
  action: SPKJobModalActionsTypes;
}) => {
  const userType = getUserType();
  const dispatch = useAppDispatch();
  const [t] = useTranslation('global');

  const optionsSelect = Object.values(EducationDegreeTypes).map(
    (singleValue) => ({ id: singleValue, text: singleValue })
  );

  const autocompleteJobTitleRef = useRef<AutoComplete>(null);
  const autocompleteRequiredSkillsRef = useRef<AutoComplete>(null);
  const autocompleteNiceToHaveRef = useRef<AutoComplete>(null);
  const modalContainerRef = useRef<HTMLFieldSetElement>(null);

  const [selectedCandidates, setSelectedCandidates] = useState<RosterData[]>(
    []
  );

  /* Company */
  const [companySuggestions, setCompanySuggestions] = useState<
    PositionDto['Company'][]
  >([]);

  /* Job title */
  const [jobTitlesSuggestions, setJobTitlesSuggestions] = useState<
    JobPosition[]
  >([]);

  /* Required Skills */
  const [requiredSkillsSuggestions, setRequiredSkillsSuggestions] = useState<
    AutocompleteOption[]
  >([]);
  const [requiredSkillsQuery, setRequiredSkillsQuery] = useState('');

  /* Nice to have skills */
  const [niceToHaveSkillsSuggestions, setNiceToHaveSkillsSuggestions] =
    useState<AutocompleteOption[]>([]);
  const [niceToHaveSkillsQuery, setNiceToHaveSkillsQuery] = useState('');

  const [showContent, setShowContent] = useState<boolean>(false);

  const { data: jobPositions } = useGetJobPositionsIdAndTitleQuery({ t });
  const { data: skillsOptions } = useGetSkillsQuery({ t });
  const { data: countriesData } = useGetAllCountriesQuery();
  const [editPositionEnhanced] = useEditPositionEnhancedMutation();
  const { data: rosterData } = useLoadRosterQuery(position.Id, {
    skip: !(
      userType === UserTypes.INTERNAL &&
      action === SPKJobModalActionsTypes.duplicate
    ),
  });
  const { data: companiesData } = useLoadCompaniesQuery(undefined, {
    skip: userType === UserTypes.EMPLOYER,
  });
  const [duplicatePositionEmployer] = useDuplicatePositionEmployerMutation();
  const [duplicatePositionInternal] = useDuplicatePositionInternalMutation();
  const [duplicatePositionWithCustomRoster] =
    useDuplicatePositionWithCustomRosterMutation();
  const [savePositionEnhanced] = useSavePositionEnhancedMutation();
  const { data: tenantData = [] } = useGetAllTenantsQuery(undefined, {
    skip: userType !== UserTypes.INTERNAL,
  });
  const { data: languagesData = [] } = useGetAllLanguagesQuery();
  const { data: smartInterviewsData = [] } = useGetAllSmartInterviewsQuery(
    undefined,
    { skip: userType !== UserTypes.INTERNAL }
  );

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors, isDirty, dirtyFields },
    watch,
  } = useForm<EditPositionFormDto>({
    defaultValues: {
      ...position,
      Company:
        action === SPKJobModalActionsTypes.duplicate ? {} : position.Company,
      Name: position.Name || '',
      BonusDetails: position.BonusDetails || '',
      BenefitsDetails: position.BenefitsDetails || '',
      CommissionDetails: position.CommissionDetails || '',
      OtherCompensation: position.OtherCompensation || '',
      ...(action === SPKJobModalActionsTypes.duplicate && {
        DuplicationMethod: DuplicateRosterPositionValues.DontDuplicate,
        DuplicateRosterData: '',
      }),
      ...(userType === UserTypes.INTERNAL && {
        DealId: position.DealId || '',
      }),
      JobPosition: position.CustomJobTitle ? {} : position.JobPosition,
      JobTypeIsntListed: !!position.CustomJobTitle,
      CustomJobTitle: position.CustomJobTitle || '',
      IsPublic: !!position.IsPublic,
      Languages: position.PositionLanguages
        ? position.PositionLanguages.map((pl) => pl.Language.Id)
        : [],
      Country: position.Country ? position.Country.Id : null,
      ShowInterestedToEmployer: position.ShowInterestedToEmployer || false,
      FeeAmount: position.FeeAmount ?? getTenantDefaultFee(),
    },
  });

  const responsibilities = register('Responsibilities', {
    required: 'Responsibilities is required',
  });

  const jobDescription = register('JobDescription');
  const {
    RequiredSkills: requiredSkillsValue,
    NiceToHaveSkills: niceToHaveSkillsValue,
    SalaryLowerBound: salaryLowerBound,
    SalaryUpperBound: salaryUpperBound,
    DuplicationMethod: duplicationMethodValue,
    JobTypeIsntListed: jobTypeIsntListedValue,
    EnableSmartInterviews: enableSmartInterviewsValue,
    TenantId: tenantIdValue,
  } = watch();

  const tenantDefaultFee = useMemo(() => {
    const tenantName = tenantData.find(
      (singleTenant) => singleTenant.Id === Number(tenantIdValue)
    )?.title;
    return getTenantDefaultFee(tenantName);
  }, [tenantData, tenantIdValue]);

  useEffect(() => {
    if (userType !== UserTypes.INTERNAL) return;
    if (!dirtyFields.TenantId) return;
    if (dirtyFields.FeeAmount) return;
    setValue<'FeeAmount'>('FeeAmount', tenantDefaultFee, {
      shouldDirty: false,
    });
  }, [
    userType,
    tenantDefaultFee,
    dirtyFields.TenantId,
    dirtyFields.FeeAmount,
    setValue,
  ]);
  const degreeName = register('DegreeName');

  const autocompleteCompanySearch = async ({
    query,
  }: AutoCompleteCompleteEvent) => {
    const matchedSuggestion = companiesData?.filter((opt) => {
      const optionMatches =
        opt.Name.toLowerCase().indexOf(query.toLowerCase()) >= 0;

      return optionMatches;
    });
    setCompanySuggestions(matchedSuggestion || []);
  };

  const autocompleteRequiredSkillsSearch = async ({
    query,
  }: AutoCompleteCompleteEvent) => {
    setRequiredSkillsQuery(query.toLowerCase());
    const matchedSuggestion = skillsOptions?.filter((opt) => {
      const optionMatches =
        opt.title.toLowerCase().indexOf(query.toLowerCase()) >= 0;
      const optionIsAlreadySelected = requiredSkillsValue?.find(
        (item) => item.Title?.toLowerCase() === opt.title?.toLowerCase()
      );
      const optionIsANiceToHaveSkill = niceToHaveSkillsValue?.find(
        (item) => item.Title?.toLowerCase() === opt.title?.toLowerCase()
      );

      return (
        optionMatches && !optionIsAlreadySelected && !optionIsANiceToHaveSkill
      );
    });
    setRequiredSkillsSuggestions(
      matchedSuggestion ? [...matchedSuggestion] : []
    );
  };

  const autocompleteNiceToHaveSkillsSearch = async ({
    query,
  }: AutoCompleteCompleteEvent) => {
    setNiceToHaveSkillsQuery(query.toLowerCase());
    const matchedSuggestion = skillsOptions?.filter((opt) => {
      const optionMatches =
        opt.title.toLowerCase().indexOf(query.toLowerCase()) >= 0;
      const optionIsAlreadySelected = niceToHaveSkillsValue?.find(
        (item) => item.Title?.toLowerCase() === opt.title?.toLowerCase()
      );
      const optionIsARequiredSkill = requiredSkillsValue?.find(
        (item) => item.Title?.toLowerCase() === opt.title?.toLowerCase()
      );

      return (
        optionMatches && !optionIsAlreadySelected && !optionIsARequiredSkill
      );
    });
    setNiceToHaveSkillsSuggestions(
      matchedSuggestion ? [...matchedSuggestion] : []
    );
  };

  const autocompletejobTitlesSearch = async (ev: AutoCompleteCompleteEvent) => {
    const matchedSuggestion = jobPositions?.filter(
      (opt) => opt.title.toLowerCase().indexOf(ev.query.toLowerCase()) >= 0
    );
    setJobTitlesSuggestions(matchedSuggestion ? [...matchedSuggestion] : []);
  };

  const jobTitleSelectHandler = (
    e: AutoCompleteSelectEvent,
    field: ControllerRenderProps<EditPositionFormDto, 'JobPosition.title'>
  ) => {
    const picked = e.value as AutocompleteOption;
    field.onChange(picked.title);
    setValue('JobPosition.Id' as Path<EditPositionFormDto>, picked.Id, {
      shouldDirty: true,
    });
  };

  const jobTitleBlurHandler = (
    field: ControllerRenderProps<EditPositionFormDto, 'JobPosition.title'>
  ) => {
    const typed = field.value as string;
    const match = (jobPositions ?? []).find((x) => x.title === typed);

    if (match) {
      setValue('JobPosition.Id' as Path<EditPositionFormDto>, match.Id, {
        shouldDirty: true,
      });
      field.onChange(match.title);
    }

    field.onBlur();
  };

  const save = async (data: any) => {
    /* Just to remove some props that aren't needed on duplicate endpoint, if added backend throws error */
    const {
      CandidatesPositionStatus,
      positionSkills,
      DuplicationMethod,
      JobDescriptionFileUrl,
      JobDescriptionFileKey,
      ...payload
    } = data;

    if (!payload.CustomJobTitle) {
      payload.CustomJobTitle = null;
    } else {
      payload.IsCustomPosition = true;
    }

    /* Remove tenant id if not set */
    if (payload.TenantId < 0) delete payload.TenantId;

    /* Transform Languages array into the required format */
    if (payload.Languages && Array.isArray(payload.Languages)) {
      payload.Languages = payload.Languages.map((languageId: number) => ({
        languageId,
        IsRequired: true,
      }));
    }

    if (payload.SeniorityLevel) {
      // If user has selected a seniority level, add the experience years too
      payload.ExperienceYears = seniorityToExpYears(payload.SeniorityLevel);
    } else if (
      /** Edits an old position that doesnt have seniority level */
      position.ExperienceYears &&
      position.ExperienceYears > 0 &&
      !payload.SeniorityLevel
    ) {
      payload.SeniorityLevel = expYearsToSeniority(payload.ExperienceYears);
    }

    if (action === SPKJobModalActionsTypes.duplicate) {
      let duplicateResponse;
      if (userType === UserTypes.EMPLOYER) {
        duplicateResponse = await duplicatePositionEmployer({ position: data });
      }

      if (userType === UserTypes.INTERNAL) {
        /** Duplicate all or none roster */
        if (
          DuplicationMethod === DuplicateRosterPositionValues.DontDuplicate ||
          DuplicationMethod === DuplicateRosterPositionValues.DuplicateRoster
        ) {
          payload.DuplicateRoster =
            DuplicationMethod === DuplicateRosterPositionValues.DuplicateRoster;
          duplicateResponse = await duplicatePositionInternal({
            position: payload,
          });
        } else if (
          /** Paste ids */
          DuplicationMethod === DuplicateRosterPositionValues.PasteIds
        ) {
          const Candidates = payload.DuplicateRosterData.split(/\n|","/g).map(
            (singleCandidateId: string) => parseInt(singleCandidateId, 10)
          );
          duplicateResponse = await duplicatePositionWithCustomRoster({
            position: payload,
            candidateId: Candidates,
          });
        } else if (
          /** Select rosters */
          DuplicationMethod === DuplicateRosterPositionValues.SelectRoster
        ) {
          const Candidates = selectedCandidates.map(({ id }) => id);
          duplicateResponse = await duplicatePositionWithCustomRoster({
            position: payload,
            candidateId: Candidates,
          });
        }
        if (onDuplicate) onDuplicate();
      }

      if (duplicateResponse && 'data' in duplicateResponse) {
        SPKToast(
          t('EmployerAddPosition.Success.PositionDuplicatedSuccessfully')
        );
        closeHandler();
      } else {
        SPKToast(t('EmployerAddPosition.Errors.SomethingWentWrong'), {
          type: 'Error',
        });
      }
    } else if (action === SPKJobModalActionsTypes.edit) {
      const editedPositionData = await editPositionEnhanced({
        ...payload,
        positionId: position.Id,
      } as EditPositionEnhancedInput);
      if ('data' in editedPositionData) {
        if (userType === UserTypes.INTERNAL)
          dispatch(
            updateStatePosition(
              editedPositionData.data as unknown as PositionDto
            )
          );
        SPKToast(
          userLanguage === ValidLanguages.es
            ? SuccessMessageEs.EditPosition
            : SuccessMessageEn.EditPosition
        );
        closeHandler();
      } else {
        SPKToast(t('EmployerAddPosition.Errors.SomethingWentWrong'), {
          type: 'Error',
        });
      }
    } else if (
      action === SPKJobModalActionsTypes.create &&
      userType === UserTypes.INTERNAL
    ) {
      delete payload.Id;
      const response = await savePositionEnhanced(payload);
      if ('data' in response) {
        const newPositionData: PositionDto = { ...response.data };
        newPositionData.CandidatesPositionStatus = [];
        dispatch(
          companySlice.actions.addPosition(
            newPositionData as unknown as PositionDto
          )
        );
        SPKToast(
          userLanguage === ValidLanguages.es
            ? SuccessMessageEs.SavePosition
            : SuccessMessageEn.SavePosition
        );
        closeHandler();
      } else {
        SPKToast(t('EmployerAddPosition.Errors.SomethingWentWrong'), {
          type: 'Error',
        });
      }
    }
  };

  const confirmCancel = () => {
    if (isDirty)
      confirmDialog({
        message: t('CancelModal.discard'),
        defaultFocus: 'reject',
        acceptLabel: t('CancelModal.Cancel'),
        rejectLabel: t('CancelModal.continue'),
        accept: closeHandler,
        rejectClassName:
          'text-employer-primary border border-solid border-employer-primary',
        acceptClassName: 'text-white bg-spk-brightRed',
        contentClassName: 'flex justify-center font-bold text-xl',
        className: 'shadow-none',
      });
    else closeHandler();
  };

  const errorHandler = (err: FieldErrors<EditPositionFormDto>) => {
    // If errors are required skills or position name, scroll to top since they dont have RHF ref so wont focus by itself
    const erroredProperties = Object.keys(err);
    const isARequiredFieldThatWontFocus = erroredProperties.some((errorKey) =>
      ['JobPosition', 'RequiredSkills', 'Company', 'SeniorityLevel'].includes(
        errorKey
      )
    );
    const isSeniorityLevelError =
      erroredProperties.indexOf('SeniorityLevel') !== -1;

    if (modalContainerRef.current && isARequiredFieldThatWontFocus) {
      if (isSeniorityLevelError && erroredProperties.length === 1) {
        setShowContent(true);
        modalContainerRef.current
          .querySelector("label[for='SeniorityLevel']")
          ?.scrollIntoView({ behavior: 'smooth', inline: 'nearest' });
      } else {
        modalContainerRef.current?.scrollTo({ behavior: 'smooth', top: 0 });
      }
    }
  };

  return (
    <div
      data-testid="SPKJobModal"
      className="bg-white p-4 md:p-8 rounded-xl font-mulish w-11/12 xl:w-1/2 max-h-[90%] flex flex-col"
    >
      <div className="flex flex-col flex-none">
        <div className="flex">
          <h1
            className="text-2xl font-extrabold mb-2"
            data-testid="SPKJobModal-header"
          >
            {action === SPKJobModalActionsTypes.duplicate &&
              t('PositionCard.DuplicatePosition')}
            {action === SPKJobModalActionsTypes.edit &&
              t('EmployerAddPosition.EditTitle')}
            {action === SPKJobModalActionsTypes.create &&
              t('EmployerAddPosition.AddTitle')}
          </h1>
          <button
            type="button"
            className="text-spk-mediumContrast text-sm flex flex-row justify-end ml-auto items-start"
            onClick={() => confirmCancel()}
          >
            <span className="flex items-center gap-1">
              {t('common.close')}
              <CloseIcon className="stroke-spk-mediumContrast w-4 h-4" />
            </span>
          </button>
        </div>

        <h3 className="text-spk-mediumContrast text-sm mb-4">
          {action === SPKJobModalActionsTypes.duplicate &&
            t('EmployerAddPosition.DuplicateText')}
          {action === SPKJobModalActionsTypes.edit &&
            t('EmployerAddPosition.EditText')}
          {action === SPKJobModalActionsTypes.create &&
            t('EmployerAddPosition.AddText')}
        </h3>
      </div>
      <form
        onSubmit={handleSubmit(save, errorHandler)}
        className="flex flex-col overflow-hidden"
      >
        <div
          className="flex flex-col gap-4 flex-1 overflow-auto"
          role="group"
          aria-labelledby="form-title"
        >
          {userType === UserTypes.INTERNAL && (
            <div data-testid="SPKJobModal-Company">
              <Controller
                name="Company"
                control={control}
                rules={{
                  validate: (e) => {
                    return (
                      !!e?.Name || t('EmployerAddPosition.Errors.RequiredField')
                    );
                  },
                }}
                render={({ field }) => {
                  return (
                    <div className="flex flex-col gap-1 flex-1">
                      <label
                        htmlFor={field.name}
                        className="text-sm font-normal text-spk-mediumContrast"
                      >
                        {t('EmployerAddPosition.Company')}
                      </label>
                      <AutoComplete
                        {...field}
                        id={field.name}
                        className="w-full"
                        placeholder={t(
                          'EmployerAddPosition.CompanyNamePlaceholder'
                        )}
                        forceSelection
                        field="Name"
                        suggestions={companySuggestions || []}
                        onChange={(e) => field.onChange(e.value)}
                        completeMethod={autocompleteCompanySearch}
                        pt={{
                          input: {
                            root: {
                              className: 'w-full',
                            },
                          },
                        }}
                      />
                      {(errors?.Company as any)?.message && (
                        <p className="text-xs text-spk-brightRed">
                          {(errors?.Company as any)?.message}
                        </p>
                      )}
                    </div>
                  );
                }}
              />
            </div>
          )}

          {jobTypeIsntListedValue ? (
            <Controller
              control={control}
              key="CustomJobTitle"
              name="CustomJobTitle"
              rules={{
                required: t('EmployerAddPosition.Errors.RequiredField'),
              }}
              render={({ field }) => (
                <div className="flex flex-col gap-1 flex-1">
                  <label
                    htmlFor={field.name}
                    className="text-sm font-normal text-spk-mediumContrast"
                  >
                    {t('EmployerAddPosition.JobType')}
                  </label>
                  <InputText
                    id={field.name}
                    placeholder={t(
                      'EmployerAddPosition.PositionInputPlaceholder'
                    )}
                    autoComplete="off"
                    {...field}
                  />
                </div>
              )}
            />
          ) : (
            <Controller
              key="JobPosition.title"
              name="JobPosition.title"
              control={control}
              rules={{
                required: 'Job Position is required',
                validate: (value) => {
                  const matchedPosition = jobPositions?.find(
                    (singleJobPosition) => singleJobPosition.title === value
                  );
                  return (
                    !!matchedPosition ||
                    t('EmployerAddPosition.Errors.InvalidJobPosition')
                  );
                },
              }}
              render={({ field }) => {
                return (
                  <div className="flex flex-col gap-1 flex-1">
                    <label
                      htmlFor={field.name}
                      className="text-sm font-normal text-spk-mediumContrast"
                    >
                      {t('EmployerAddPosition.PositionName')}
                    </label>
                    <AutoComplete
                      emptyMessage={t(
                        'EmployerAddPosition.JobPositionNotFound'
                      )}
                      showEmptyMessage
                      id={field.name}
                      className="w-full"
                      placeholder={t(
                        'EmployerAddPosition.PositionNamePlaceholder'
                      )}
                      value={field.value}
                      onChange={(e) => field.onChange(e.value)}
                      onSelect={(e) => jobTitleSelectHandler(e, field)}
                      onBlur={() => jobTitleBlurHandler(field)}
                      ref={autocompleteJobTitleRef}
                      suggestions={jobTitlesSuggestions}
                      completeMethod={autocompletejobTitlesSearch}
                      onClick={(e) =>
                        autocompleteJobTitleRef.current?.search(
                          e,
                          field.value,
                          'dropdown'
                        )
                      }
                      itemTemplate={(item) => item.title}
                      pt={{
                        input: {
                          root: {
                            className: 'w-full',
                          },
                        },
                      }}
                    />
                    {(errors?.JobPosition as any)?.title?.message && (
                      <p className="text-xs text-spk-brightRed">
                        {(errors?.JobPosition as any)?.title?.message}
                      </p>
                    )}
                  </div>
                );
              }}
            />
          )}
          <div className="flex gap-8">
            <Controller
              control={control}
              name="JobTypeIsntListed"
              render={({ field }) => (
                <div className="flex flex-row items-center gap-2">
                  <Checkbox
                    {...field}
                    checked={field.value}
                    inputId={field.name}
                    onChange={({ value }) => {
                      field.onChange(!value);

                      // Cleans custom job title
                      if (value) {
                        setValue('CustomJobTitle', '' as unknown as never);
                      }
                    }}
                  />
                  <label htmlFor={field.name} className="text-sm">
                    {t('EmployerAddPosition.JobTypeIsntListed')}
                  </label>
                </div>
              )}
            />
            {userType === UserTypes.INTERNAL && (
              <>
                <Controller
                  control={control}
                  name="IsPublic"
                  render={({ field }) => (
                    <div className="flex flex-row items-center gap-2">
                      <Checkbox
                        {...field}
                        checked={field.value}
                        inputId={field.name}
                        onChange={({ value }) => field.onChange(!value)}
                      />
                      <label htmlFor={field.name} className="text-sm">
                        {t('EmployerAddPosition.IsPublic')}
                      </label>
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name="ShowInterestedToEmployer"
                  render={({ field }) => (
                    <div className="flex flex-row items-center gap-2">
                      <Checkbox
                        {...field}
                        checked={field.value}
                        inputId={field.name}
                        onChange={({ value }) => field.onChange(!value)}
                      />
                      <label htmlFor={field.name} className="text-sm">
                        {t('EmployerAddPosition.ShowClientInterested')}
                      </label>
                    </div>
                  )}
                />
              </>
            )}
          </div>
          <div className="flex flex-row gap-8">
            {userType === UserTypes.INTERNAL && (
              <>
                <Controller
                  control={control}
                  name="DealId"
                  render={({ field }) => (
                    <div
                      className="flex flex-col gap-1 w-full"
                      data-testid="SPKJobModal-DealId"
                    >
                      <label
                        htmlFor={field.name}
                        className="text-sm font-normal text-spk-mediumContrast"
                      >
                        {t('EmployerAddPosition.DealId')}
                      </label>
                      <InputText
                        {...field}
                        placeholder={t('EmployerAddPosition.DealId')}
                        autoComplete="off"
                      />
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name="TenantId"
                  rules={{
                    required: t('EmployerAddPosition.Errors.RequiredField'),
                  }}
                  render={({ field }) => (
                    <div
                      className="flex flex-col gap-1 w-full"
                      data-testid="SPKJobModal-TenantId"
                    >
                      <label
                        htmlFor={field.name}
                        className="text-sm font-normal text-spk-mediumContrast"
                      >
                        {t('EmployerAddPosition.Organization')}
                      </label>
                      <Dropdown
                        {...field}
                        className="w-full"
                        options={tenantData}
                        optionLabel="title"
                        optionValue="Id"
                        placeholder={t(
                          'EmployerAddPosition.Placeholders.SelectAnOption'
                        )}
                      />
                      {errors.TenantId?.message && (
                        <p className="text-spk-brightRed text-xs">
                          {errors.TenantId?.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </>
            )}
          </div>
          <div className="flex flex-row gap-8">
            <Controller
              control={control}
              name="Country"
              render={({ field }) => {
                return (
                  <div
                    className="flex flex-col gap-1 w-full"
                    data-testid="SPKJobModal-Country"
                  >
                    <label
                      htmlFor={field.name}
                      className="text-sm font-normal text-spk-mediumContrast"
                    >
                      {t('EmployerAddPosition.PositionCountry')}
                    </label>
                    <Dropdown
                      {...field}
                      className="w-full"
                      options={countriesData}
                      optionLabel="NiceName"
                      optionValue="Id"
                      filter
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.SelectAnOption'
                      )}
                    />
                    {errors.Country?.message && (
                      <p className="text-spk-brightRed text-xs">
                        {errors.Country?.message}
                      </p>
                    )}
                  </div>
                );
              }}
            />
            <Controller
              control={control}
              name="Languages"
              render={({ field }) => (
                <div
                  className="flex flex-col gap-1 w-full"
                  data-testid="SPKJobModal-Languages"
                >
                  <label
                    htmlFor={field.name}
                    className="text-sm font-normal text-spk-mediumContrast"
                  >
                    {t('EmployerAddPosition.Languages')}
                  </label>
                  <MultiSelect
                    onChange={(e) => field.onChange(e.value)}
                    value={field.value}
                    className="w-full"
                    options={languagesData}
                    optionLabel="Name"
                    optionValue="Id"
                    placeholder={t(
                      'EmployerAddPosition.Placeholders.SelectAnOption'
                    )}
                    display="chip"
                    showClear={false}
                    pt={{
                      header: {
                        className: 'hidden',
                      },
                    }}
                    maxSelectedLabels={1}
                  />
                  {errors.Languages?.message && (
                    <p className="text-spk-brightRed text-xs">
                      {errors.Languages?.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <label
              htmlFor={responsibilities.name}
              className="text-sm font-normal text-spk-mediumContrast"
            >
              {t('EmployerAddPosition.PositionResponsibilities')}
            </label>
            <Controller
              name="Responsibilities"
              control={control}
              render={({ field }) => (
                <PrimeReactEditor
                  name={field.name}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                  ref={field.ref}
                  value={field.value}
                />
              )}
            />
            {errors.Responsibilities?.message && (
              <p className="text-spk-brightRed text-xs">
                {errors.Responsibilities?.message}
              </p>
            )}
          </div>

          <Controller
            control={control}
            name="RequiredSkills"
            rules={{
              validate: (value) =>
                value && value.length > 0
                  ? true
                  : 'Please select at least one skill',
            }}
            render={({ field, formState }) => (
              <div className="flex flex-col gap-1 flex-1">
                <label
                  htmlFor={field.name}
                  className="text-sm font-normal text-spk-mediumContrast"
                >
                  {t('EmployerAddPosition.RequiredSkills')}
                </label>
                <AutoComplete
                  id={field.name}
                  className="w-full"
                  placeholder={
                    field.value && field.value.length === 0
                      ? t('EmployerAddPosition.PositionSkillsPlaceHolder')
                      : ''
                  }
                  value={field.value ?? []}
                  field="title"
                  onChange={field.onChange}
                  ref={autocompleteRequiredSkillsRef}
                  onClick={(e) =>
                    autocompleteRequiredSkillsRef.current?.search(
                      e,
                      requiredSkillsQuery,
                      'dropdown'
                    )
                  }
                  virtualScrollerOptions={{
                    itemSize: 38,
                    scrollHeight: `${
                      Math.min(requiredSkillsSuggestions?.length || 0, 5) *
                      (requiredSkillsSuggestions.length > 2 ? 45 : 50)
                    }px`,
                  }}
                  selectedItemTemplate={(item) => item.title ?? item.Title}
                  multiple
                  suggestions={requiredSkillsSuggestions}
                  completeMethod={autocompleteRequiredSkillsSearch}
                  pt={{
                    container: {
                      className: 'w-full',
                    },
                  }}
                />
                {formState.errors?.RequiredSkills && (
                  <p className="text-xs text-spk-brightRed">
                    {(formState.errors.RequiredSkills as any).message}
                  </p>
                )}
              </div>
            )}
          />
          <div className="flex gap-6 items-start">
            <div className="flex flex-col flex-1">
              <p className="text-sm font-semibold text-spk-highContrast">
                {t('EmployerAddPosition.PositionSalary')}:
              </p>
              <div className="flex gap-2">
                <Controller
                  control={control}
                  name="SalaryLowerBound"
                  rules={{
                    min: {
                      value: MINIMUM_BASE_COMPENSATION,
                      message: `Salary lower bound must be greater than ${MINIMUM_BASE_COMPENSATION}`,
                    },
                    validate: (value) =>
                      value < salaryUpperBound ||
                      'The lower bound must be less than the upper bound',
                  }}
                  render={({ field, formState }) => (
                    <div className="flex flex-col gap-1 flex-1">
                      <label
                        htmlFor={field.name}
                        className="text-sm font-normal text-spk-mediumContrast"
                      >
                        {t('EmployerAddPosition.BudgetMin')}
                      </label>
                      <InputText
                        id={field.name}
                        value={`$${field.value
                          .toLocaleString()
                          .replace(',', '.')}`}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setValue(
                            'SalaryLowerBound',
                            Number(value) as unknown as never,
                            {
                              shouldDirty: true,
                              shouldValidate: true,
                            }
                          );
                        }}
                        autoComplete="off"
                      />
                      {formState.errors.SalaryLowerBound && (
                        <p className="text-xs text-spk-brightRed">
                          {formState.errors.SalaryLowerBound.message}
                        </p>
                      )}
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name="SalaryUpperBound"
                  rules={{
                    min: {
                      value: 0,
                      message: 'Salary upper bound must be greater than 0',
                    },
                    validate: (value) =>
                      value > salaryLowerBound ||
                      'The upper bound must be greater than the lower bound',
                  }}
                  render={({ field, formState }) => (
                    <div className="flex flex-col gap-1 flex-1">
                      <label
                        htmlFor={field.name}
                        className="text-sm font-normal text-spk-mediumContrast"
                      >
                        {t('EmployerAddPosition.BudgetMax')}
                      </label>
                      <InputText
                        id={field.name}
                        value={`$${field.value
                          .toLocaleString()
                          .replace(',', '.')}`}
                        onChange={async (e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          await setValue(
                            'SalaryUpperBound',
                            Number(value) as unknown as never,
                            {
                              shouldDirty: true,
                              shouldValidate: true,
                            }
                          );
                          // reset the error in the lower bound
                          setValue(
                            'SalaryLowerBound',
                            salaryLowerBound as unknown as never,
                            {
                              shouldDirty: true,
                              shouldValidate: true,
                            }
                          );
                        }}
                        autoComplete="off"
                      />
                      {formState.errors.SalaryUpperBound && (
                        <p className="text-xs text-spk-brightRed">
                          {formState.errors.SalaryUpperBound.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>
            {userType === UserTypes.INTERNAL && (
              <div className="flex flex-col gap-1 flex-1 max-w-56">
                <p className="text-xs font-semibold text-spk-highContrast">
                  {/* This p tag is just to keep aligned everything */}
                  &nbsp;
                </p>
                <label
                  htmlFor="FeeAmount"
                  className="text-sm font-normal text-spk-mediumContrast"
                >
                  {t('EmployerAddPosition.SimeraFee')}
                </label>
                <Controller
                  control={control}
                  name="FeeAmount"
                  rules={{
                    validate: (value) =>
                      (value != null && Number(value) >= 0) ||
                      t('EmployerAddPosition.Errors.RequiredField'),
                  }}
                  render={({ field, formState }) => (
                    <>
                      <InputText
                        id="FeeAmount"
                        value={
                          field.value != null
                            ? `$${Number(field.value)
                                .toLocaleString()
                                .replace(',', '.')}`
                            : ''
                        }
                        placeholder={`$${tenantDefaultFee.toLocaleString(
                          'en-US'
                        )}`}
                        onChange={(e) => {
                          const numeric = e.target.value.replace(/[^0-9]/g, '');
                          field.onChange(
                            numeric === '' ? null : Number(numeric)
                          );
                        }}
                        autoComplete="off"
                      />
                      {formState.errors.FeeAmount && (
                        <p className="text-xs text-spk-brightRed">
                          {formState.errors.FeeAmount.message as string}
                        </p>
                      )}
                    </>
                  )}
                />
              </div>
            )}
          </div>
          {userType === UserTypes.INTERNAL && (
            <div className="flex gap-4">
              <div className="flex flex-col gap-1 w-1/4">
                <label
                  htmlFor="EnableSmartInterviews"
                  className="text-sm font-normal text-spk-mediumContrast"
                >
                  Enable Smart Interviews
                </label>
                <Controller
                  control={control}
                  name="EnableSmartInterviews"
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      inputId="EnableSmartInterviews"
                      className="w-full"
                      options={[
                        { name: 'Yes', value: true },
                        { name: 'No', value: false },
                      ]}
                      optionLabel="name"
                      optionValue="value"
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.SelectAnOption'
                      )}
                    />
                  )}
                />
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <label
                  htmlFor="SmartInterviewQuestionId"
                  className="text-sm font-normal text-spk-mediumContrast"
                >
                  {t('EmployerAddPosition.SmartInterview')}
                </label>
                <Controller
                  control={control}
                  name="SmartInterviewQuestionId"
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      inputId="SmartInterviewQuestionId"
                      className="w-full"
                      options={smartInterviewsData.map((si) => ({
                        label: `${si.Id} - ${si.Name}`,
                        value: si.Id,
                      }))}
                      optionLabel="label"
                      optionValue="value"
                      filter
                      disabled={!enableSmartInterviewsValue}
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.SelectAnOption'
                      )}
                    />
                  )}
                />
              </div>
            </div>
          )}
          {/* Seniority for internal */}
          {userType === UserTypes.INTERNAL && (
            <div
              className="gap-1 flex flex-col flex-1"
              data-testid="SPKJobModal-Seniority-Internal"
            >
              <label
                htmlFor="SeniorityLevel"
                className="text-sm font-normal text-spk-mediumContrast"
              >
                {t('EmployerAddPosition.Seniority')}
              </label>
              <Controller
                control={control}
                name="SeniorityLevel"
                rules={{
                  validate: (value) => {
                    if (action === SPKJobModalActionsTypes.create) {
                      return (
                        !!value || t('EmployerAddPosition.Errors.RequiredField')
                      );
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <SelectButton
                    {...field}
                    options={Object.values(SeniorityLevelType)}
                    allowEmpty={false}
                  />
                )}
              />
              {errors.SeniorityLevel && (
                <p className="text-xs text-spk-brightRed">
                  {errors.SeniorityLevel.message}
                </p>
              )}
            </div>
          )}

          <button
            className="font-semibold flex items-center gap-4"
            type="button"
            onClick={() => setShowContent((v) => !v)}
            data-testid="SPKJobModal-AdditionalDetails-Button"
          >
            {t('EmployerAddPosition.AdditionalDetails')}
            {!showContent ? <ArrowDown /> : <ArrowUp />}
          </button>

          <div
            className={`${showContent ? 'flex' : 'hidden'} flex-col gap-4`}
            data-testid="SPKJobModal-AdditionalDetails-Container"
          >
            <div className="flex flex-row gap-4">
              {/* Type of work */}
              <div className="flex flex-col flex-1 gap-1">
                <label
                  className="text-sm font-normal text-spk-mediumContrast"
                  htmlFor="TypeOfWork"
                >
                  {t('EmployerAddPosition.TypeOfWork')}
                </label>
                <Controller
                  control={control}
                  name="TypeOfWork"
                  render={({ field }) => (
                    <SelectButton
                      {...field}
                      options={Object.values(TypesOfWork)}
                      allowEmpty={false}
                    />
                  )}
                />
              </div>
              {/* Seniority for employer */}
              {userType === UserTypes.EMPLOYER && (
                <div className="gap-1 flex flex-col flex-1">
                  <label
                    htmlFor="SeniorityLevel"
                    className="text-sm font-normal text-spk-mediumContrast"
                  >
                    {t('EmployerAddPosition.Seniority')}
                  </label>
                  <Controller
                    control={control}
                    name="SeniorityLevel"
                    rules={{
                      validate: (value) => {
                        if (action === SPKJobModalActionsTypes.create) {
                          return (
                            !!value ||
                            t('EmployerAddPosition.Errors.RequiredField')
                          );
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <SelectButton
                        {...field}
                        options={Object.values(SeniorityLevelType)}
                        allowEmpty={false}
                      />
                    )}
                  />
                  {errors.SeniorityLevel && (
                    <p className="text-xs text-spk-brightRed">
                      {errors.SeniorityLevel.message}
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-row gap-4">
              {/* Work Typ */}
              <div className="flex flex-col flex-1 gap-1">
                <label
                  className="text-sm font-normal text-spk-mediumContrast"
                  htmlFor="WorkLocation"
                >
                  {t('EmployerAddPosition.WorkType')}
                </label>
                <Controller
                  control={control}
                  name="WorkLocation"
                  render={({ field }) => (
                    <SelectButton
                      {...field}
                      options={Object.values(WorkLocationTypes)}
                      allowEmpty={false}
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex flex-row gap-4">
              {/* Ideal start date */}
              <div className="flex flex-col flex-1 gap-1">
                <label
                  htmlFor="IdealStartDate"
                  className="text-sm font-normal text-spk-mediumContrast"
                >
                  {t('EmployerAddPosition.IdeaStartDate')}
                </label>
                <Controller
                  control={control}
                  name="IdealStartDate"
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      options={Object.values(IdealStartDateValues)}
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.SelectAnOption'
                      )}
                    />
                  )}
                />
              </div>
              {/* Working Hours */}
              <div className="flex flex-col flex-1 gap-1">
                <label
                  htmlFor="WorkingHours"
                  className="text-sm font-normal text-spk-mediumContrast"
                >
                  {t('EmployerAddPosition.WorkingHours')}
                </label>
                <Controller
                  control={control}
                  name="WorkingHours"
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      options={TIMEZONES}
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.SelectAnOption'
                      )}
                    />
                  )}
                />
              </div>
              {/* Working Days */}
              <div className="flex flex-col flex-1 gap-1">
                <label
                  htmlFor="WorkingDays"
                  className="text-sm font-normal text-spk-mediumContrast"
                >
                  {t('EmployerAddPosition.WorkingDays')}
                </label>
                <Controller
                  control={control}
                  name="WorkingDays"
                  render={({ field }) => (
                    <Dropdown
                      {...field}
                      options={Object.values(WorkingDaysValues)}
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.SelectAnOption'
                      )}
                    />
                  )}
                />
              </div>
            </div>
            <Controller
              control={control}
              name="NiceToHaveSkills"
              render={({ field }) => (
                <div className="flex flex-col gap-1 flex-1">
                  <label
                    htmlFor={field.name}
                    className="text-sm font-normal text-spk-mediumContrast"
                  >
                    {t('EmployerAddPosition.NiceToHaveSkills')}
                  </label>
                  <AutoComplete
                    id={field.name}
                    className="w-full"
                    placeholder={
                      field.value && field.value.length === 0
                        ? t('EmployerAddPosition.PositionSkillsPlaceHolder')
                        : ''
                    }
                    value={field.value ?? []}
                    field="title"
                    onChange={field.onChange}
                    ref={autocompleteNiceToHaveRef}
                    onClick={(e) =>
                      autocompleteNiceToHaveRef.current?.search(
                        e,
                        niceToHaveSkillsQuery,
                        'dropdown'
                      )
                    }
                    virtualScrollerOptions={{
                      itemSize: 38,
                      scrollHeight: `${
                        Math.min(niceToHaveSkillsSuggestions?.length || 0, 5) *
                        (niceToHaveSkillsSuggestions.length > 2 ? 45 : 50)
                      }px`,
                    }}
                    selectedItemTemplate={(item) => item.title || item.Title}
                    multiple
                    suggestions={niceToHaveSkillsSuggestions}
                    completeMethod={autocompleteNiceToHaveSkillsSearch}
                    pt={{
                      container: {
                        className: 'w-full',
                      },
                    }}
                  />
                </div>
              )}
            />
            <div className="flex justify-between gap-8">
              <div className="flex flex-col flex-1 gap-2">
                <label
                  htmlFor="BonusDetails"
                  className="text-sm font-normal text-spk-mediumContrast"
                >
                  Bonus
                </label>
                <Controller
                  control={control}
                  name="BonusDetails"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.RoughIdea'
                      )}
                      autoComplete="off"
                    />
                  )}
                />
              </div>
              <div className="flex flex-col flex-1 gap-2">
                <label
                  htmlFor="CommissionDetails"
                  className="text-sm font-normal text-spk-mediumContrast"
                >
                  {t('EmployerAddPosition.Commission')}
                </label>
                <Controller
                  control={control}
                  name="CommissionDetails"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.RoughIdea'
                      )}
                      autoComplete="off"
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex justify-between gap-8">
              <div className="flex flex-col flex-1 gap-1">
                <label
                  htmlFor="BenefitsDetails"
                  className="text-sm font-normal text-spk-mediumContrast"
                >
                  {t('EmployerAddPosition.Benefits')}
                </label>
                <Controller
                  control={control}
                  name="BenefitsDetails"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.RoughIdea'
                      )}
                      autoComplete="off"
                    />
                  )}
                />
              </div>
              <div className="flex flex-col flex-1 gap-1">
                <label
                  htmlFor="OtherCompensation"
                  className="text-sm font-normal text-spk-mediumContrast"
                >
                  {t('EmployerAddPosition.Other')}
                </label>
                <Controller
                  control={control}
                  name="OtherCompensation"
                  render={({ field }) => (
                    <InputText
                      {...field}
                      placeholder={t(
                        'EmployerAddPosition.Placeholders.RoughIdea'
                      )}
                      autoComplete="off"
                    />
                  )}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="Name"
                className="text-sm font-normal text-spk-mediumContrast"
              >
                {t('EmployerAddPosition.JobTitleOptional')}
              </label>
              <Controller
                control={control}
                name="Name"
                render={({ field }) => (
                  <InputText
                    id={field.name}
                    placeholder={t(
                      'EmployerAddPosition.PositionInputPlaceholder'
                    )}
                    {...field}
                  />
                )}
              />
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <label
                htmlFor={jobDescription.name}
                className="text-sm font-normal text-spk-mediumContrast"
              >
                {t('EmployerAddPosition.PositionObjective')}
              </label>
              <Controller
                name="JobDescription"
                control={control}
                render={({ field }) => (
                  <PrimeReactEditor
                    name={field.name}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    ref={field.ref}
                    value={field.value}
                  />
                )}
              />
            </div>
            <div className="flex gap-4">
              <Controller
                name="DegreeType"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col gap-1 flex-1">
                    <label
                      htmlFor={field.name}
                      className="text-sm font-normal text-spk-mediumContrast"
                    >
                      {t('EmployerAddPosition.PositionDegreeType')}
                    </label>
                    <Dropdown
                      {...field}
                      id={field.name}
                      options={optionsSelect}
                      optionLabel="text"
                      optionValue="id"
                      placeholder="Select Degree Type"
                      itemTemplate={(item) => item.text}
                    />
                  </div>
                )}
              />

              <div className="flex flex-col gap-1 flex-1">
                <label
                  htmlFor={degreeName.name}
                  className="text-sm font-normal text-spk-mediumContrast"
                >
                  {t('EmployerAddPosition.PositionDegreeName')}
                </label>
                <InputText
                  id={degreeName.name}
                  placeholder={t(
                    'EmployerAddPosition.PositionInputPlaceholder'
                  )}
                  onChange={degreeName.onChange}
                  onBlur={degreeName.onBlur}
                  ref={degreeName.ref}
                  name={degreeName.name}
                />
              </div>
            </div>
            {position.JobDescriptionFileUrl && (
              <a href={position.JobDescriptionFileUrl}>
                <div className="bg-spk-neutral-20 rounded-full py-2 px-4 text-sm flex justify-between text-spk-mediumContrast">
                  <span>{t('EmployerAddPosition.DownloadJobDescription')}</span>
                  <i className="pi pi-download" />
                </div>
              </a>
            )}
          </div>

          {action === SPKJobModalActionsTypes.duplicate &&
            userType === UserTypes.INTERNAL && (
              <div className="flex flex-col gap-4">
                <h3 className="font-semibold">Roster</h3>
                <Controller
                  control={control}
                  name="DuplicationMethod"
                  render={({ field }) => (
                    <div className="flex flex-row gap-12 items-center">
                      <div className="flex gap-2 items-center">
                        <RadioButton
                          {...field}
                          inputId={`${field.name}_${DuplicateRosterPositionValues.DuplicateRoster}`}
                          value={DuplicateRosterPositionValues.DuplicateRoster}
                          checked={
                            field.value ===
                            DuplicateRosterPositionValues.DuplicateRoster
                          }
                        />
                        <label
                          htmlFor={`${field.name}_${DuplicateRosterPositionValues.DuplicateRoster}`}
                        >
                          {DuplicateRosterPositionValues.DuplicateRoster}
                        </label>
                      </div>
                      <div className="flex gap-2 items-center">
                        <RadioButton
                          {...field}
                          inputId={`${field.name}_${DuplicateRosterPositionValues.SelectRoster}`}
                          value={DuplicateRosterPositionValues.SelectRoster}
                          checked={
                            field.value ===
                            DuplicateRosterPositionValues.SelectRoster
                          }
                        />
                        <label
                          htmlFor={`${field.name}_${DuplicateRosterPositionValues.SelectRoster}`}
                        >
                          {DuplicateRosterPositionValues.SelectRoster}
                        </label>
                      </div>
                      <div className="flex gap-2 items-center">
                        <RadioButton
                          {...field}
                          inputId={`${field.name}_${DuplicateRosterPositionValues.PasteIds}`}
                          value={DuplicateRosterPositionValues.PasteIds}
                          checked={
                            field.value ===
                            DuplicateRosterPositionValues.PasteIds
                          }
                        />
                        <label
                          htmlFor={`${field.name}_${DuplicateRosterPositionValues.PasteIds}`}
                        >
                          {DuplicateRosterPositionValues.PasteIds}
                        </label>
                      </div>
                      <div className="flex gap-2 items-center">
                        <RadioButton
                          {...field}
                          inputId={`${field.name}_${DuplicateRosterPositionValues.DontDuplicate}`}
                          value={DuplicateRosterPositionValues.DontDuplicate}
                          checked={
                            field.value ===
                            DuplicateRosterPositionValues.DontDuplicate
                          }
                        />
                        <label
                          htmlFor={`${field.name}_${DuplicateRosterPositionValues.DontDuplicate}`}
                        >
                          {DuplicateRosterPositionValues.DontDuplicate}
                        </label>
                      </div>
                    </div>
                  )}
                />
                <div>
                  {duplicationMethodValue ===
                    DuplicateRosterPositionValues.SelectRoster && (
                    <DataTable
                      value={rosterData}
                      selection={selectedCandidates}
                      onSelectionChange={(e) => {
                        setSelectedCandidates(
                          e.value as unknown as RosterData[]
                        );
                      }}
                      scrollable
                      scrollHeight="200px"
                      selectionMode={null}
                      size="small"
                    >
                      <Column selectionMode="multiple" />
                      <Column field="id" header="Candidate Id" />
                      <Column field="name" header="Name" />
                      <Column field="activerosters" header="Active Rosters" />
                      <Column
                        field="activeinterviews"
                        header="Active Interviews"
                      />
                    </DataTable>
                  )}
                  {duplicationMethodValue ===
                    DuplicateRosterPositionValues.PasteIds && (
                    <Controller
                      control={control}
                      name="DuplicateRosterData"
                      render={({ field }) => (
                        <FloatLabel className="mt-4">
                          <InputTextarea
                            {...field}
                            id={field.name}
                            className="w-full"
                          />
                          <label htmlFor={field.name}>
                            Paste candidate IDs separated by lines or commas
                          </label>
                        </FloatLabel>
                      )}
                    />
                  )}
                </div>
              </div>
            )}
        </div>
        <div className="w-full flex items-center justify-end gap-4 flex-none mt-4">
          <Button
            type="button"
            label={t('common.cancel')}
            onClick={confirmCancel}
            severity="success"
            outlined
          />
          <Button type="submit" label={t('common.save')} severity="success" />
        </div>
      </form>
      <ConfirmDialog />
    </div>
  );
};

SPKJobModal.defaultProps = {
  onDuplicate: () => {},
};
export default SPKJobModal;
